import imgCropUrl from '@/home/utils/imgCropUrl';
import TrackWrapper from '~/components/common/TrackWrapper';
import { FeedItemType } from '~/constants/home';
import { useIsMobile } from '~/context/common/DeviceContext';
import { BffFeedCardProps } from '~/contracts/home';
import { useHandleNativePurchaseOnClick } from '~/native/home/hooks';
import { defaultImageHandler } from '../../utils/defaultImageHandler';
import formatEvent from '../../utils/formatEvent';
import {
  Card,
  CardButtonBlue,
  CardDescription,
  CardHeader,
  CardImageSection,
  CardInfoSection,
  TrimMileage,
  VehicleDetails,
  VehicleImage,
  YearMakeModel
} from '../OrderStatusCards.styles';

function PipVehicleDetails({ feedItem }: BffFeedCardProps) {
  const handleNativePurchaseOnClick = useHandleNativePurchaseOnClick();

  if (
    feedItem.content == null ||
    feedItem.content.vehicle == null ||
    feedItem.callToAction == null
  ) {
    return null;
  }

  const {
    callToAction,
    content: {
      vehicle: {
        make,
        model,
        trim,
        year,
        mileage = 0,
        id: vehicleId,
        imageUrl,
        vehicleInventoryType,
        stockNumber
      }
    }
  } = feedItem;

  const nativePayload = {
    make,
    model,
    trim,
    year,
    vehicleId,
    vehicleImage: imageUrl,
    inventoryType: vehicleInventoryType,
    stockNumber
  };

  return (
    <>
      <VehicleDetails>
        <YearMakeModel data-qa="purchase-in-progress-year-make-model">
          {year} {make} {model}
        </YearMakeModel>
        <TrimMileage data-qa="purchase-in-progress-trim">
          {trim}
          <b>&nbsp;&nbsp;•&nbsp;&nbsp;</b>
          {mileage.toLocaleString()} miles
        </TrimMileage>
      </VehicleDetails>
      <CardButtonBlue
        href="/purchase"
        data-analytics-id="Home feed card"
        data-qa="Home purchase in progress card - continue purchase CTA"
        data-analytics-attributes={JSON.stringify({
          description: 'Purchase in progress cta',
          vehicleId
        })}
        data-native-included="false"
        onClick={event => handleNativePurchaseOnClick(event, nativePayload)}
      >
        {callToAction.title}
      </CardButtonBlue>
    </>
  );
}

export default function PurchaseInProgressCard({ feedItem }: BffFeedCardProps) {
  const isMobile = useIsMobile();
  const handleNativePurchaseOnClick = useHandleNativePurchaseOnClick();

  if (
    feedItem.content == null ||
    feedItem.content.vehicle == null ||
    feedItem.callToAction == null
  ) {
    return null;
  }

  const {
    type,
    callToAction,
    content: {
      vehicle: {
        imageUrl,
        make,
        model,
        trim,
        id: vehicleId,
        year,
        vehicleInventoryType,
        stockNumber
      }
    }
  } = feedItem;

  const customAttributes = {
    description: 'Purchase in progress cta',
    vehicleId
  };

  const descriptionTextMap = {
    [FeedItemType.TRADE_PURCHASE_IN_PROGRESS]:
      'You have a few more steps to complete your trade-in before time runs out!',
    [FeedItemType.RETAIL_PURCHASE_IN_PROGRESS]:
      'You have a few more steps to complete your purchase before time runs out!'
  };

  const nativePayload = {
    make,
    model,
    trim,
    year,
    vehicleId,
    vehicleImage: imageUrl,
    inventoryType: vehicleInventoryType,
    stockNumber
  };

  return (
    <TrackWrapper
      event={formatEvent('Viewed - Home feed section', customAttributes)}
    >
      <Card data-qa="purchase-in-progress-card">
        <CardInfoSection>
          <CardHeader data-qa="purchase-in-progress-title">
            This {make} is almost yours!
          </CardHeader>
          <CardDescription data-qa="purchase-in-progress-text">
            {descriptionTextMap[type]}
          </CardDescription>
          {!isMobile && <PipVehicleDetails feedItem={feedItem} />}
        </CardInfoSection>
        <CardImageSection>
          <a
            href={callToAction.link}
            data-qa="purchase-in-progress-image-container"
            data-analytics-id="Home feed card"
            data-analytics-attributes={JSON.stringify(customAttributes)}
            data-native-included="false"
            onClick={event => handleNativePurchaseOnClick(event, nativePayload)}
          >
            <picture>
              <source
                srcSet={imgCropUrl(imageUrl, false)}
                media="(min-width=768px)"
              />
              <VehicleImage
                data-qa="purchase-in-progress-image"
                alt={`${make} ${model}`}
                src={imgCropUrl(imageUrl, true)}
                onError={defaultImageHandler}
              />
            </picture>
          </a>
          {isMobile && <PipVehicleDetails feedItem={feedItem} />}
        </CardImageSection>
      </Card>
    </TrackWrapper>
  );
}
